import mitt, { Emitter } from 'mitt' // Usaremos mitt para un event emitter simple y tipado

// --- Configuración ---
const WATCHER_WS_URL_BASE = process.env.WATCHER_WS_URL || 'ws://localhost:3010'
const RECONNECT_DELAY = 5000 // ms

// --- Tipos ---
// Tipos de eventos que emitirá nuestro servicio
export type WebSocketEvent = {
  // Mensaje específico recibido del watcher
  messageReceived: { type: string; payload: any }
  // Eventos del ciclo de vida de la conexión
  connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'error'
  // Eventos específicos de RFID
  rfidAccess: any
}

// Tipos para los mensajes que esperamos del Watcher
// Lo exportamos para que los componentes puedan usarlo
export interface DeviceLastConnectionMessage {
  type: 'deviceLastConnection'
  app: string
  city: string
  name: string
  date: string
}

// Podríamos añadir más interfaces para otros tipos como 'deviceData', etc.

// Interfaz base del mensaje recibido (para tipar el JSON parseado)
interface BaseWatcherMessage {
  type: string
  [key: string]: any
}

interface NormalizedWatcherPayload {
  deviceId?: string
  app?: string
  city?: string
  name?: string
  zone?: string
  date?: string
  lastConnection?: string
  [key: string]: any
}

class WebSocketService {
  private ws: WebSocket | null = null
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null
  private url: string = ''
  private token: string | null = null
  private emitter: Emitter<WebSocketEvent> = mitt<WebSocketEvent>()
  private connectionAttemptActive: boolean = false

  constructor() {
    console.log('[WebSocketService] Instantiated')
  }

  /**
   * Inicia la conexión WebSocket.
   * Debe llamarse después de que el usuario se haya autenticado y tengamos un token.
   * @param authToken El token JWT del usuario.
   */
  connect(authToken: string): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.log('WebSocket already connected.')
      return
    }
    if (!authToken) {
      console.error('WebSocketService: Auth token is required to connect.')
      return
    }
    if (this.connectionAttemptActive) {
      console.warn('WebSocketService: Connection attempt already in progress.')
      return
    }

    this.token = authToken
    this.url = `${WATCHER_WS_URL_BASE}?token=${this.token}`
    console.log(`[WebSocketService] Attempting to connect to ${this.url}`)
    this.emitter.emit('connectionStatus', 'connecting')
    this.connectionAttemptActive = true

    // Limpiar timeout anterior si existe
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout)
      this.reconnectTimeout = null
    }

    try {
      this.ws = new WebSocket(this.url)
      this.setupEventListeners()
    } catch (error) {
      console.error(
        '[WebSocketService] Error creating WebSocket instance:',
        error,
      )
      this.connectionAttemptActive = false
      this.emitter.emit('connectionStatus', 'error')
      this.scheduleReconnect()
    }
  }

  private setupEventListeners(): void {
    if (!this.ws) {
      console.error(
        '[WebSocketService] setupEventListeners called but ws is null!',
      )
      return
    }

    this.ws.onopen = () => {
      console.log('[WebSocketService] Event: open - Connection established.')
      this.connectionAttemptActive = false
      this.emitter.emit('connectionStatus', 'connected')
      // Reiniciar delay de reconexión si estábamos en ello
      if (this.reconnectTimeout) {
        clearTimeout(this.reconnectTimeout)
        this.reconnectTimeout = null
      }
    }

    this.ws.onmessage = (event) => {
      try {
        const message: BaseWatcherMessage = JSON.parse(event.data)
        const normalizedPayload = this.normalizePayload(message)

        // Emit generic event
        this.emitter.emit('messageReceived', {
          type: message.type,
          payload: normalizedPayload,
        })

        // Emit specific events based on message type
        if (message.type === 'rfidAccess') {
          this.emitter.emit('rfidAccess', message)
        }
      } catch (error) {
        console.error(
          '[WebSocketService] Error parsing message or processing:',
          error,
          'Raw data:',
          event.data,
        )
      }
    }

    this.ws.onerror = (event) => {
      console.error('[WebSocketService] Event: error', event)
      this.connectionAttemptActive = false
      this.emitter.emit('connectionStatus', 'error')
      // El evento 'close' suele dispararse después del error
    }

    this.ws.onclose = (event) => {
      console.log(
        `[WebSocketService] Event: close - Code: ${event.code}, Reason: ${event.reason || '(No reason provided)'}`,
      )
      this.connectionAttemptActive = false
      this.emitter.emit('connectionStatus', 'disconnected')
      this.ws = null

      // Intentar reconectar solo si no fue un cierre intencional o un error de autenticación
      if (event.code !== 1000 && event.code !== 1008) {
        // 1000 = Normal Closure, 1008 = Policy Violation (Auth Failed)
        this.scheduleReconnect()
      } else {
        console.log(
          "[WebSocketService] Won't reconnect due to code 1000 (Normal) or 1008 (Auth)",
        )
        this.token = null
      }
    }
  }

  private normalizePayload(
    message: BaseWatcherMessage,
  ): NormalizedWatcherPayload {
    const payloadSource =
      message.payload && typeof message.payload === 'object'
        ? message.payload
        : message

    const payload: NormalizedWatcherPayload = { ...payloadSource }

    if (!payload.deviceId && payload.app && payload.city && payload.name) {
      payload.deviceId = `${payload.app}/${payload.city}/${payload.name}`
    }

    if (!payload.date && payload.lastConnection) {
      payload.date = payload.lastConnection
    }

    return payload
  }

  /**
   * Programa un intento de reconexión.
   */
  private scheduleReconnect(): void {
    if (this.reconnectTimeout) return // Ya programado
    if (!this.token) {
      console.log(
        'WebSocketService: Not scheduling reconnect, no token available.',
      )
      return
    }

    console.log(
      `[WebSocketService] Scheduling reconnect in ${RECONNECT_DELAY / 1000} seconds...`,
    )
    this.reconnectTimeout = setTimeout(() => {
      this.reconnectTimeout = null
      this.connect(this.token!) // Reintentar con el último token válido
    }, RECONNECT_DELAY)
  }

  /**
   * Cierra la conexión WebSocket intencionalmente.
   */
  disconnect(): void {
    console.log('[WebSocketService] Disconnecting intentionally...')
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout)
      this.reconnectTimeout = null
    }
    if (this.ws) {
      this.ws.close(1000, 'User disconnected') // Código 1000 para cierre normal
    }
    this.ws = null
    this.token = null
    this.emitter.emit('connectionStatus', 'disconnected')
  }

  /**
   * Permite a los componentes suscribirse a eventos específicos.
   * Ejemplo: webSocketService.on('messageReceived', (payload) => { ... });
   * Ejemplo: webSocketService.on('connectionStatus', (status) => { ... });
   */
  on<Key extends keyof WebSocketEvent>(
    type: Key,
    handler: (payload: WebSocketEvent[Key]) => void,
  ): void {
    this.emitter.on(type, handler)
  }

  /**
   * Permite a los componentes de-suscribirse.
   */
  off<Key extends keyof WebSocketEvent>(
    type: Key,
    handler: (payload: WebSocketEvent[Key]) => void,
  ): void {
    this.emitter.off(type, handler)
  }

  /**
   * Verifica si el WebSocket está actualmente conectado.
   * @returns true si está conectado, false en caso contrario
   */
  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN
  }

  // Podríamos añadir un método sendMessage si el frontend necesita enviar datos al watcher
  // sendMessage(data: any): void { ... }
}

// Exportar una única instancia (Singleton) para que toda la app use la misma conexión
export const webSocketService = new WebSocketService()
